import { getApiHost } from '@/appSettings';
import { RestManager } from '@/newApi/services/rest/RestManager';
import { queryOptions } from '@tanstack/vue-query';
import type { QueryOptions } from '@tanstack/vue-query';
import { useLoginStore } from '@/login/stores/useLoginStore';
import { GdprMatchType, GdprUnavailableReason, PaybandUnavailableReason } from '@sysarb/sysarb-types-v2/services/v1/base-registry/customInvolve';
import type { CustomInvolveResponse } from '@sysarb/sysarb-types-v2/services/v1/base-registry/customInvolve';
import { CustomizationInvolveViewIdentifier } from '@sysarb/sysarb-types/general/enums/customEmployment';
import { usePackage } from '@/composables/usePackage';
import { useSettings } from '@/login/composables/useSettings';

export const customEmploymentSimulateOptions = {
    simulateError: false,
    simulateMockData: false,
    simulateNullState: false,
    simulateIndividualOnlyAverages: false,
    simulateMaleAverageOnly: false,
    // Verification aid for SYS-14857: when true, mock the grade + job profile grouping
    // (composite groupName) instead of the default grade/position-class grouping.
    simulateJobProfileMatch: false,
    paybandAvailable: true,
    gdprAvailable: true,
};

function buildMockData(): CustomInvolveResponse {
    const { simulateNullState, simulateIndividualOnlyAverages, simulateMaleAverageOnly, simulateJobProfileMatch, paybandAvailable, gdprAvailable } = customEmploymentSimulateOptions;
    const mockMatchType = simulateJobProfileMatch ? GdprMatchType.GRADE_JOBPROFILE_CLASS : GdprMatchType.GRADE_POSITION_CLASS;
    const mockGroupName = simulateJobProfileMatch ? '46-HE-POL_MAIN' : 'D:56';

    if (simulateNullState) {
        return {
            name: 'David Williams',
            email: 'david.williams@example.com',
            currency: 'USD',
            totalSalaryYearly: 66700,
            grade: null,
            compaRatio: null,
            salaryDate: null,
            salaryBreakdown: {
                baseSalary: null,
                baseSalaryPercentageOfTotal: null,
                salaryIncrement: null,
                salaryIncrementPercentageOfTotal: null,
                salaryIncrementSecond: null,
                salaryIncrementSecondPercentageOfTotal: null,
                salaryIncrementThird: null,
                salaryIncrementThirdPercentageOfTotal: null,
                equity: null,
                equityPercentageOfTotal: null,
                bonus: null,
                bonusPercentageOfTotal: null,
                oneTimeBonus: null,
                oneTimeBonusPercentageOfTotal: null,
                benefitsInKind: null,
                benefitsInKindPercentageOfTotal: null,
                overtime: null,
                overtimePercentageOfTotal: null,
                pension: null,
                pensionPercentageOfTotal: null,
                paidHoliday: null,
                paidHolidayPercentageOfTotal: null,
                carAllowance: null,
                carAllowancePercentageOfTotal: null,
            },
            paybandSection: paybandAvailable
                ? { available: true, min: 35000, median: 56000, max: 70000 }
                : { available: false, reasonCode: PaybandUnavailableReason.NO_DATA, reason: 'No payband data available for this grade.' },
            gdprSection: gdprAvailable
                ? {
                    available: true,
                    matchType: mockMatchType,
                    groupName: mockGroupName,
                    averageFemaleSalaryBreakdown: null,
                    averageMaleSalaryBreakdown: null,
                    unadjustedPayGapComparedToMeanFemaleTotalSalary: null,
                    unadjustedPayGapComparedToMeanMaleTotalSalary: null,
                    unadjustedPayGapComparedToMeanFemaleBaseSalary: null,
                    unadjustedPayGapComparedToMeanMaleBaseSalary: null,
                }
                : { available: false, reasonCode: GdprUnavailableReason.INSUFFICIENT_DATA, reason: 'Not enough data to display GDPR comparison.', matchType: mockMatchType, groupName: mockGroupName },
        };
    }

    if (simulateIndividualOnlyAverages) {
        return {
            name: 'David Williams',
            email: 'david.williams@example.com',
            currency: 'USD',
            totalSalaryYearly: 66700,
            grade: 'D:56',
            compaRatio: 1.15,
            salaryDate: null,
            salaryBreakdown: {
                baseSalary: 52000,
                baseSalaryPercentageOfTotal: 0.779,
                salaryIncrement: 3000,
                salaryIncrementPercentageOfTotal: 0.045,
                salaryIncrementSecond: null,
                salaryIncrementSecondPercentageOfTotal: null,
                salaryIncrementThird: null,
                salaryIncrementThirdPercentageOfTotal: null,
                equity: 2000,
                equityPercentageOfTotal: 0.030,
                bonus: 4500,
                bonusPercentageOfTotal: 0.067,
                oneTimeBonus: null,
                oneTimeBonusPercentageOfTotal: null,
                benefitsInKind: 800,
                benefitsInKindPercentageOfTotal: 0.012,
                overtime: null,
                overtimePercentageOfTotal: null,
                pension: 3200,
                pensionPercentageOfTotal: 0.048,
                paidHoliday: null,
                paidHolidayPercentageOfTotal: null,
                carAllowance: 1200,
                carAllowancePercentageOfTotal: 0.018,
            },
            paybandSection: paybandAvailable
                ? { available: true, min: 35000, median: 56000, max: 70000 }
                : { available: false, reasonCode: PaybandUnavailableReason.NO_DATA, reason: 'No payband data available for this grade.' },
            gdprSection: gdprAvailable
                ? {
                    available: true,
                    matchType: mockMatchType,
                    groupName: mockGroupName,
                    averageFemaleSalaryBreakdown: null,
                    averageMaleSalaryBreakdown: null,
                    unadjustedPayGapComparedToMeanFemaleTotalSalary: null,
                    unadjustedPayGapComparedToMeanMaleTotalSalary: null,
                    unadjustedPayGapComparedToMeanFemaleBaseSalary: null,
                    unadjustedPayGapComparedToMeanMaleBaseSalary: null,
                }
                : { available: false, reasonCode: GdprUnavailableReason.INSUFFICIENT_DATA, reason: 'Not enough data to display GDPR comparison.', matchType: mockMatchType, groupName: mockGroupName },
        };
    }

    if (simulateMaleAverageOnly) {
        const maleSalaryBreakdown = {
            baseSalary: 53000,
            baseSalaryPercentageOfTotal: 0.794,
            salaryIncrement: 3000,
            salaryIncrementPercentageOfTotal: 0.045,
            salaryIncrementSecond: null,
            salaryIncrementSecondPercentageOfTotal: null,
            salaryIncrementThird: null,
            salaryIncrementThirdPercentageOfTotal: null,
            equity: 2000,
            equityPercentageOfTotal: 0.030,
            bonus: 5000,
            bonusPercentageOfTotal: 0.075,
            oneTimeBonus: null,
            oneTimeBonusPercentageOfTotal: null,
            benefitsInKind: 800,
            benefitsInKindPercentageOfTotal: 0.012,
            overtime: null,
            overtimePercentageOfTotal: null,
            pension: 3200,
            pensionPercentageOfTotal: 0.048,
            paidHoliday: null,
            paidHolidayPercentageOfTotal: null,
            carAllowance: 1200,
            carAllowancePercentageOfTotal: 0.018,
        };
        return {
            name: 'David Williams',
            email: 'david.williams@example.com',
            totalSalaryYearly: 66700,
            grade: 'D:56',
            currency: 'USD',
            compaRatio: 1.15,
            salaryDate: null,
            salaryBreakdown: {
                baseSalary: 52000,
                baseSalaryPercentageOfTotal: 0.779,
                salaryIncrement: 3000,
                salaryIncrementPercentageOfTotal: 0.045,
                salaryIncrementSecond: null,
                salaryIncrementSecondPercentageOfTotal: null,
                salaryIncrementThird: null,
                salaryIncrementThirdPercentageOfTotal: null,
                equity: 2000,
                equityPercentageOfTotal: 0.030,
                bonus: 4500,
                bonusPercentageOfTotal: 0.067,
                oneTimeBonus: null,
                oneTimeBonusPercentageOfTotal: null,
                benefitsInKind: 800,
                benefitsInKindPercentageOfTotal: 0.012,
                overtime: null,
                overtimePercentageOfTotal: null,
                pension: 3200,
                pensionPercentageOfTotal: 0.048,
                paidHoliday: null,
                paidHolidayPercentageOfTotal: null,
                carAllowance: 1200,
                carAllowancePercentageOfTotal: 0.018,
            },
            paybandSection: paybandAvailable
                ? { available: true, min: 35000, median: 56000, max: 70000 }
                : { available: false, reasonCode: PaybandUnavailableReason.NO_DATA, reason: 'No payband data available for this grade.' },
            gdprSection: gdprAvailable
                ? {
                    available: true,
                    matchType: mockMatchType,
                    groupName: mockGroupName,
                    averageFemaleSalaryBreakdown: null,
                    averageMaleSalaryBreakdown: maleSalaryBreakdown,
                    unadjustedPayGapComparedToMeanFemaleTotalSalary: null,
                    unadjustedPayGapComparedToMeanMaleTotalSalary: -0.02,
                    unadjustedPayGapComparedToMeanFemaleBaseSalary: null,
                    unadjustedPayGapComparedToMeanMaleBaseSalary: -0.018,
                }
                : { available: false, reasonCode: GdprUnavailableReason.INSUFFICIENT_DATA, reason: 'Not enough data to display GDPR comparison.', matchType: mockMatchType, groupName: mockGroupName },
        };
    }

    const salaryBreakdown = {
        baseSalary: 52000,
        baseSalaryPercentageOfTotal: 0.779,
        salaryIncrement: 3000,
        salaryIncrementPercentageOfTotal: 0.045,
        salaryIncrementSecond: null,
        salaryIncrementSecondPercentageOfTotal: null,
        salaryIncrementThird: null,
        salaryIncrementThirdPercentageOfTotal: null,
        equity: 2000,
        equityPercentageOfTotal: 0.030,
        bonus: 4500,
        bonusPercentageOfTotal: 0.067,
        oneTimeBonus: null,
        oneTimeBonusPercentageOfTotal: null,
        benefitsInKind: 800,
        benefitsInKindPercentageOfTotal: 0.012,
        overtime: null,
        overtimePercentageOfTotal: null,
        pension: 3200,
        pensionPercentageOfTotal: 0.048,
        paidHoliday: null,
        paidHolidayPercentageOfTotal: null,
        carAllowance: 1200,
        carAllowancePercentageOfTotal: 0.018,
    };

    return {
        name: 'David Williams',
        email: 'david.williams@example.com',
        totalSalaryYearly: 66700,
        currency: 'USD',
        grade: 'D:56',
        compaRatio: 1.15,
        salaryBreakdown,
        salaryDate: null,
        paybandSection: paybandAvailable
            ? { available: true, min: 35000, median: 56000, max: 70000 }
            : { available: false, reasonCode: PaybandUnavailableReason.NO_DATA, reason: 'No payband data available for this grade.' },
        gdprSection: gdprAvailable
            ? {
                available: true,
                matchType: mockMatchType,
                groupName: mockGroupName,
                averageFemaleSalaryBreakdown: { ...salaryBreakdown, baseSalary: 48000, bonus: 3800 },
                averageMaleSalaryBreakdown: { ...salaryBreakdown, baseSalary: 53000, bonus: 5000 },
                unadjustedPayGapComparedToMeanFemaleTotalSalary: 0.08,
                unadjustedPayGapComparedToMeanMaleTotalSalary: -0.02,
                unadjustedPayGapComparedToMeanFemaleBaseSalary: 0.075,
                unadjustedPayGapComparedToMeanMaleBaseSalary: -0.018,
            }
            : { available: false, reasonCode: GdprUnavailableReason.INSUFFICIENT_DATA, reason: 'Not enough data to display GDPR comparison.', matchType: mockMatchType, groupName: mockGroupName },
    };
}

const baseRegistryService = new RestManager(`https://${getApiHost()}/base-registry-service`);

type CustomEmploymentOptions = {
    queries: {
        getCustomInvolve: (employmentId: string | null | undefined) => QueryOptions<CustomInvolveResponse, Error>;
    };
};

export const customEmployment: CustomEmploymentOptions = {
    queries: {
        getCustomInvolve: (employmentId: string | null | undefined): QueryOptions<CustomInvolveResponse, Error> => {
            const loginStore = useLoginStore();
            const { instance } = loginStore;
            return queryOptions<CustomInvolveResponse, Error>({
                queryKey: ['customInvolve', instance, employmentId],
                queryFn: async (): Promise<CustomInvolveResponse> => {
                    if (customEmploymentSimulateOptions.simulateError) {
                        throw new Error('Simulated error for customInvolve');
                    }
                    if (customEmploymentSimulateOptions.simulateMockData) {
                        return buildMockData();
                    }
                    try {
                        return await baseRegistryService.get<CustomInvolveResponse>(`v1/${instance}/involve/custom/employment/${employmentId}`);
                    } catch (error) {
                        console.error('getCustomInvolve', error);
                        throw error;
                    }
                },
                enabled: () => {
                    if (employmentId == null) return false;
                    const { enableInvolvePortalEmployee } = usePackage();
                    const { customizationSettings } = useSettings();
                    if (!enableInvolvePortalEmployee.value) return false;
                    return customizationSettings.value?.customizationEmploymentViewIdentifier === CustomizationInvolveViewIdentifier.HIT_INVOLVE_001;
                },
            });
        },
    },
};
